"use client";

import * as React from "react";
import { IconCheck, IconChevronDown, IconClose } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import type { SelectOption } from "./select";

type Size = "xs" | "sm" | "md";

interface ComboboxProps {
  value: string | number;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  size?: Size;
  align?: "start" | "end" | "center";
  clearable?: boolean;
}

const SIZE_TRIGGER: Record<Size, string> = {
  xs: "px-2 py-1 text-[11px] gap-1",
  sm: "px-2.5 py-1.5 text-[11px] gap-1.5",
  md: "px-3 py-2.5 text-[12px] gap-2",
};

const SIZE_CHEVRON: Record<Size, number> = { xs: 10, sm: 12, md: 14 };

export function Combobox({
  value,
  onChange,
  options,
  placeholder = "Выбрать…",
  searchPlaceholder = "Поиск…",
  emptyText = "Ничего не найдено",
  className,
  contentClassName,
  disabled,
  size = "md",
  align = "start",
  clearable = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => String(o.value) === String(value));

  // Group options if any have `group`
  const hasGroups = options.some((o) => o.group);
  const groups: Array<{ label?: string; items: SelectOption[] }> = [];
  if (hasGroups) {
    const ungrouped = options.filter((o) => !o.group);
    if (ungrouped.length) groups.push({ items: ungrouped });
    const map = new Map<string, SelectOption[]>();
    for (const o of options) {
      if (!o.group) continue;
      if (!map.has(o.group)) map.set(o.group, []);
      map.get(o.group)!.push(o);
    }
    for (const [label, items] of map) groups.push({ label, items });
  } else {
    groups.push({ items: options });
  }

  function handleSelect(v: string) {
    onChange(v);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] text-left transition-colors outline-none",
            "hover:border-[var(--primary)]/30",
            "data-[state=open]:border-[var(--primary)]/50 data-[state=open]:ring-2 data-[state=open]:ring-[var(--primary)]/10",
            "disabled:cursor-not-allowed disabled:opacity-50",
            SIZE_TRIGGER[size],
            className,
          )}
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate",
              selected ? "text-[var(--text)]" : "text-[var(--textMuted)]",
            )}
          >
            {selected?.label ?? placeholder}
          </span>
          {clearable && selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange("");
                }
              }}
              className="shrink-0 text-[var(--textMuted)] hover:text-[var(--text)] transition"
            >
              <IconClose size={SIZE_CHEVRON[size]} />
            </span>
          )}
          <IconChevronDown
            size={SIZE_CHEVRON[size]}
            className={cn(
              "shrink-0 text-[var(--textMuted)] transition-transform duration-150",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={4}
        className={cn("w-[var(--radix-popover-trigger-width)] p-0", contentClassName)}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {groups.map((g, gi) => (
              <CommandGroup key={gi} heading={g.label}>
                {g.items.map((o) => {
                  const isSelected = String(o.value) === String(value);
                  return (
                    <CommandItem
                      key={o.value}
                      value={`${o.label} ${o.value}`}
                      disabled={o.disabled}
                      onSelect={() => handleSelect(String(o.value))}
                      data-state={isSelected ? "checked" : undefined}
                      className={cn(
                        isSelected &&
                          "bg-[var(--secondary)] text-[var(--secondaryText)] data-[selected=true]:bg-[var(--secondary)]",
                      )}
                    >
                      <span className="flex-1 truncate">{o.label}</span>
                      {isSelected && <IconCheck size={12} className="shrink-0 text-[var(--primary)]" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
